import { eq, inArray, useLiveQuery } from "@tanstack/react-db";
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useSyncExternalStore,
} from "react";
import type { PerformanceChartResolution } from "@repo/types";
import { useAppSessionScope } from "~/lib/app-session-scope";
import {
  combineCollectionQueryError,
  combineCollectionQueryStatus,
  getCollectionLastError,
  normalizeCollectionError,
} from "~/lib/tanstack-db-collection-errors";
import {
  buildPortfolioDetailViewQuery,
  getPortfolioDomainCollections,
  hydratePortfolioSummaries,
  subscribeToPortfolioDomainCollections,
  type PortfolioDomainCollections,
} from "./collections";
import {
  normalizePortfolioAddress,
  type PortfolioAllocationHistoryCheckpointModel,
  type PortfolioAssetModel,
  type PortfolioAssetPnlModel,
  type PortfolioAssetPriceModel,
  type PortfolioDashboardAssetSummaryModel,
  type PortfolioHistoryEventModel,
  type PortfolioInstanceModel,
  type PortfolioPerformancePointModel,
  type PortfolioPerformanceSeriesPointModel,
  type PortfolioPerformanceSummaryModel,
  type PortfolioPerformanceWindowStatsModel,
  type PortfolioPositionModel,
  type PortfolioScheduleModel,
  type PortfolioScheduledFunctionModel,
  type PortfolioVaultModel,
  type PortfolioWorkflowModel,
} from "./models";
import { normalizeAddressList } from "../normalize";

export interface PortfolioTokenGraphRow {
  asset?: PortfolioAssetModel;
  assetPrice?: PortfolioAssetPriceModel;
  position: PortfolioPositionModel;
}

export interface PortfolioDashboardGraphRow extends PortfolioInstanceModel {
  dashboardAssetSummaries?: PortfolioDashboardAssetSummaryModel[];
  liveTokenRows?: PortfolioTokenGraphRow[];
  performanceSeries: PortfolioPerformanceSeriesPointModel[];
  performanceWindowStats?: PortfolioPerformanceWindowStatsModel;
  portfolioSchedule?: PortfolioScheduleModel;
  tokenRows: PortfolioTokenGraphRow[];
  vaults: PortfolioVaultModel[];
}

export interface PortfolioDetailGraphRow extends PortfolioDashboardGraphRow {
  assetPnl: PortfolioAssetPnlModel[];
  historyEvents: PortfolioHistoryEventModel[];
  liveTokenRows: PortfolioTokenGraphRow[];
  performancePoints: PortfolioPerformancePointModel[];
  performanceSeries: PortfolioPerformanceSeriesPointModel[];
  performanceSummary?: PortfolioPerformanceSummaryModel;
  performanceWindowStats?: PortfolioPerformanceWindowStatsModel;
  portfolioAssetRows: PortfolioTokenGraphRow[];
  portfolioSchedule?: PortfolioScheduleModel;
}

function usePortfolioGraphStoreSubscription(input: {
  enabled: boolean;
  ownerAddresses?: string[];
  sessionScopeToken: string;
}): void {
  const ownerAddressScopeKey = (input.ownerAddresses ?? []).join("|");

  useEffect(() => {
    if (!input.enabled) {
      return;
    }

    return subscribeToPortfolioDomainCollections({
      ownerAddresses: input.ownerAddresses,
      sessionScopeToken: input.sessionScopeToken,
    });
  }, [input.enabled, ownerAddressScopeKey, input.sessionScopeToken]);
}

function normalizeNonEmptyIds(values: string[] | undefined): string[] {
  const source = values ?? [];
  const seen = new Set<string>();
  const output: string[] = [];

  for (const rawValue of source) {
    const normalized = rawValue.trim();
    if (normalized.length === 0 || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    output.push(normalized);
  }

  return output;
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const deduped = new Map<string, T>();

  for (const item of items) {
    deduped.set(item.id, item);
  }

  return Array.from(deduped.values());
}

function createOwnershipSummary(
  portfolioInstances: PortfolioInstanceModel[],
  ownerAddressSet: ReadonlySet<string>,
) {
  const ownedPortfolioIds: string[] = [];
  const readOnlyPortfolioIds: string[] = [];

  for (const portfolio of portfolioInstances) {
    const explicitOwnership = portfolio.viewerIsOwner;
    const inferredOwnership = ownerAddressSet.has(
      normalizePortfolioAddress(portfolio.ownerAddress),
    );
    const isOwned = explicitOwnership ?? inferredOwnership;

    if (isOwned) {
      ownedPortfolioIds.push(portfolio.id);
    } else {
      readOnlyPortfolioIds.push(portfolio.id);
    }
  }

  return {
    ownedPortfolioCount: ownedPortfolioIds.length,
    ownedPortfolioIds,
    readOnlyPortfolioCount: readOnlyPortfolioIds.length,
    readOnlyPortfolioIds,
    refreshTier:
      ownedPortfolioIds.length > 0
        ? ("owned" as const)
        : readOnlyPortfolioIds.length > 0
          ? ("watch-only" as const)
          : ("none" as const),
  };
}

function groupRowsByPortfolioId<T extends { portfolioId: string }>(
  rows: T[],
): Map<string, T[]> {
  const grouped = new Map<string, T[]>();

  for (const row of rows) {
    const existing = grouped.get(row.portfolioId);
    if (existing) {
      existing.push(row);
      continue;
    }

    grouped.set(row.portfolioId, [row]);
  }

  return grouped;
}

function mapRowsById<T extends { id: string }>(rows: T[]): Map<string, T> {
  const mapped = new Map<string, T>();

  for (const row of rows) {
    mapped.set(row.id, row);
  }

  return mapped;
}

function mapAssetPricesByAssetId(
  assetPrices: PortfolioAssetPriceModel[],
): Map<string, PortfolioAssetPriceModel> {
  const mapped = new Map<string, PortfolioAssetPriceModel>();

  for (const assetPrice of assetPrices) {
    mapped.set(assetPrice.assetId, assetPrice);
  }

  return mapped;
}

function buildTokenRows(input: {
  assetById: ReadonlyMap<string, PortfolioAssetModel>;
  assetPriceByAssetId: ReadonlyMap<string, PortfolioAssetPriceModel>;
  positions: PortfolioPositionModel[];
}): PortfolioTokenGraphRow[] {
  return input.positions.map((position) => ({
    asset: input.assetById.get(position.assetId),
    assetPrice: input.assetPriceByAssetId.get(position.assetId),
    position,
  }));
}

function isPortfolioAssetModel(value: unknown): value is PortfolioAssetModel {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PortfolioAssetModel>;
  return (
    typeof candidate.id === "string" &&
    candidate.id.length > 0 &&
    typeof candidate.address === "string" &&
    candidate.address.length > 0 &&
    typeof candidate.chainRef === "string" &&
    candidate.chainRef.length > 0 &&
    typeof candidate.decimals === "number" &&
    Number.isFinite(candidate.decimals) &&
    typeof candidate.name === "string" &&
    candidate.name.length > 0 &&
    typeof candidate.symbol === "string" &&
    candidate.symbol.length > 0 &&
    typeof candidate.vm === "string" &&
    candidate.vm.length > 0
  );
}

function isPortfolioAssetPriceModel(
  value: unknown,
): value is PortfolioAssetPriceModel {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<PortfolioAssetPriceModel>;
  return (
    typeof candidate.id === "string" &&
    candidate.id.length > 0 &&
    typeof candidate.assetId === "string" &&
    candidate.assetId.length > 0 &&
    typeof candidate.priceUsd === "number" &&
    Number.isFinite(candidate.priceUsd) &&
    typeof candidate.updatedAt === "number" &&
    Number.isFinite(candidate.updatedAt)
  );
}

function normalizePositiveInteger(
  value: number | undefined,
  fallback: number,
): number {
  if (!Number.isFinite(value) || value === undefined) {
    return fallback;
  }

  const normalized = Math.floor(value);
  return normalized > 0 ? normalized : fallback;
}

export function usePortfolioDomainSlice(input: {
  enabled: boolean;
  ownerAddresses?: string[];
  strategyInstanceIds?: string[];
}) {
  const normalizedOwnerAddresses = useMemo(
    () => normalizeAddressList(input.ownerAddresses ?? []),
    [input.ownerAddresses],
  );
  const ownerAddressSet = useMemo(
    () => new Set(normalizedOwnerAddresses),
    [normalizedOwnerAddresses],
  );
  const scopedStrategyInstanceIds = useMemo(
    () => normalizeNonEmptyIds(input.strategyInstanceIds),
    [input.strategyInstanceIds],
  );
  const sessionScope = useAppSessionScope();
  const ownerAddressesCacheKey = normalizedOwnerAddresses.join("|");
  const scopedStrategyIdsCacheKey = scopedStrategyInstanceIds.join("|");
  const shouldUseLiveCollections = input.enabled;
  const collections = useMemo(
    () =>
      shouldUseLiveCollections
        ? getPortfolioDomainCollections({
            ownerAddresses: normalizedOwnerAddresses,
            sessionScopeToken: sessionScope.scopeToken,
          })
        : null,
    [
      normalizedOwnerAddresses,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  usePortfolioGraphStoreSubscription({
    enabled: shouldUseLiveCollections,
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });

  const portfolioInstancesQuery = useLiveQuery(
    (queryBuilder) => {
      if (!shouldUseLiveCollections || !collections) {
        return undefined;
      }

      const baseQuery = queryBuilder.from({
        portfolioInstance: collections.portfolioInstances,
      });
      const filteredQuery =
        scopedStrategyInstanceIds.length > 0
          ? baseQuery.where(({ portfolioInstance }) =>
              inArray(portfolioInstance.id, scopedStrategyInstanceIds),
            )
          : normalizedOwnerAddresses.length === 0
            ? baseQuery
            : normalizedOwnerAddresses.length === 1
              ? baseQuery.where(({ portfolioInstance }) =>
                  eq(
                    portfolioInstance.ownerAddress,
                    normalizedOwnerAddresses[0]!,
                  ),
                )
              : baseQuery.where(({ portfolioInstance }) =>
                  inArray(
                    portfolioInstance.ownerAddress,
                    normalizedOwnerAddresses,
                  ),
                );

      return filteredQuery;
    },
    [
      collections,
      ownerAddressesCacheKey,
      scopedStrategyIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );

  const portfolioInstances = useMemo(
    () => dedupeById(portfolioInstancesQuery.data ?? []),
    [portfolioInstancesQuery.data],
  );
  const portfolioIds = useMemo(
    () =>
      normalizeNonEmptyIds(portfolioInstances.map((portfolio) => portfolio.id)),
    [portfolioInstances],
  );
  const portfolioIdsCacheKey = portfolioIds.join("|");

  const positionsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ position: collections.positions })
            .where(({ position }) => eq(position.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ position: collections.positions })
            .where(({ position }) =>
              inArray(position.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const positions = useMemo(
    () => dedupeById(positionsQuery.data ?? []),
    [positionsQuery.data],
  );

  const portfolioLivePositionsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        !collections.portfolioLivePositions ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ position: collections.portfolioLivePositions })
            .where(({ position }) => eq(position.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ position: collections.portfolioLivePositions })
            .where(({ position }) =>
              inArray(position.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const portfolioLivePositions = useMemo(
    () => dedupeById(portfolioLivePositionsQuery.data ?? []),
    [portfolioLivePositionsQuery.data],
  );

  const vaultsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ vault: collections.vaults })
            .where(({ vault }) => eq(vault.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ vault: collections.vaults })
            .where(({ vault }) => inArray(vault.portfolioId, portfolioIds));
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const vaults = useMemo(
    () => dedupeById(vaultsQuery.data ?? []),
    [vaultsQuery.data],
  );

  const dashboardAssetSummariesQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ summary: collections.dashboardAssetSummaries })
            .where(({ summary }) => eq(summary.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ summary: collections.dashboardAssetSummaries })
            .where(({ summary }) => inArray(summary.portfolioId, portfolioIds));
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const dashboardAssetSummaries = useMemo(
    () => dedupeById(dashboardAssetSummariesQuery.data ?? []),
    [dashboardAssetSummariesQuery.data],
  );

  const performanceSeriesQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ performanceSeriesPoint: collections.performanceSeries })
            .where(({ performanceSeriesPoint }) =>
              eq(performanceSeriesPoint.portfolioId, portfolioIds[0]!),
            )
        : queryBuilder
            .from({ performanceSeriesPoint: collections.performanceSeries })
            .where(({ performanceSeriesPoint }) =>
              inArray(performanceSeriesPoint.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const performanceSeries = useMemo(
    () =>
      dedupeById(performanceSeriesQuery.data ?? []).sort(
        (left, right) => left.timestamp - right.timestamp,
      ),
    [performanceSeriesQuery.data],
  );

  const performanceWindowStatsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldUseLiveCollections ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ windowStats: collections.performanceWindowStats })
            .where(({ windowStats }) =>
              eq(windowStats.portfolioId, portfolioIds[0]!),
            )
        : queryBuilder
            .from({ windowStats: collections.performanceWindowStats })
            .where(({ windowStats }) =>
              inArray(windowStats.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const performanceWindowStats = useMemo(
    () => dedupeById(performanceWindowStatsQuery.data ?? []),
    [performanceWindowStatsQuery.data],
  );

  const assetIds = useMemo(
    () =>
      normalizeNonEmptyIds(
        [...positions, ...portfolioLivePositions].map(
          (position) => position.assetId,
        ),
      ),
    [portfolioLivePositions, positions],
  );
  const assetIdsCacheKey = assetIds.join("|");

  const assetsQuery = useLiveQuery(
    (queryBuilder) => {
      if (!shouldUseLiveCollections || !collections || assetIds.length === 0) {
        return undefined;
      }

      return assetIds.length === 1
        ? queryBuilder
            .from({ asset: collections.assets })
            .where(({ asset }) => eq(asset.id, assetIds[0]!))
        : queryBuilder
            .from({ asset: collections.assets })
            .where(({ asset }) => inArray(asset.id, assetIds));
    },
    [
      assetIdsCacheKey,
      collections,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const assets = useMemo(
    () => dedupeById(assetsQuery.data ?? []),
    [assetsQuery.data],
  );

  const assetPricesQuery = useLiveQuery(
    (queryBuilder) => {
      if (!shouldUseLiveCollections || !collections || assetIds.length === 0) {
        return undefined;
      }

      return assetIds.length === 1
        ? queryBuilder
            .from({ assetPrice: collections.assetPrices })
            .where(({ assetPrice }) => eq(assetPrice.assetId, assetIds[0]!))
        : queryBuilder
            .from({ assetPrice: collections.assetPrices })
            .where(({ assetPrice }) => inArray(assetPrice.assetId, assetIds));
    },
    [
      assetIdsCacheKey,
      collections,
      sessionScope.scopeToken,
      shouldUseLiveCollections,
    ],
  );
  const assetPrices = useMemo(
    () => dedupeById(assetPricesQuery.data ?? []),
    [assetPricesQuery.data],
  );

  const assetById = useMemo(() => mapRowsById(assets), [assets]);
  const assetPriceByAssetId = useMemo(
    () => mapAssetPricesByAssetId(assetPrices),
    [assetPrices],
  );
  const positionsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(positions),
    [positions],
  );
  const portfolioLivePositionsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(portfolioLivePositions),
    [portfolioLivePositions],
  );
  const vaultsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(vaults),
    [vaults],
  );
  const dashboardAssetSummariesByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(dashboardAssetSummaries),
    [dashboardAssetSummaries],
  );
  const performanceSeriesByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(performanceSeries),
    [performanceSeries],
  );
  const performanceWindowStatsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(performanceWindowStats),
    [performanceWindowStats],
  );

  const portfolios = useMemo<PortfolioDashboardGraphRow[]>(
    () =>
      portfolioInstances.map((portfolio) => ({
        ...portfolio,
        dashboardAssetSummaries:
          dashboardAssetSummariesByPortfolioId.get(portfolio.id) ?? [],
        liveTokenRows: buildTokenRows({
          assetById,
          assetPriceByAssetId,
          positions:
            portfolioLivePositionsByPortfolioId.get(portfolio.id) ?? [],
        }),
        performanceSeries:
          performanceSeriesByPortfolioId.get(portfolio.id) ?? [],
        performanceWindowStats: performanceWindowStatsByPortfolioId.get(
          portfolio.id,
        )?.[0],
        tokenRows: buildTokenRows({
          assetById,
          assetPriceByAssetId,
          positions: positionsByPortfolioId.get(portfolio.id) ?? [],
        }),
        vaults: vaultsByPortfolioId.get(portfolio.id) ?? [],
      })),
    [
      assetById,
      assetPriceByAssetId,
      dashboardAssetSummariesByPortfolioId,
      performanceSeriesByPortfolioId,
      performanceWindowStatsByPortfolioId,
      portfolioLivePositionsByPortfolioId,
      portfolioInstances,
      positionsByPortfolioId,
      vaultsByPortfolioId,
    ],
  );
  const ownership = useMemo(
    () => createOwnershipSummary(portfolioInstances, ownerAddressSet),
    [ownerAddressSet, portfolioInstances],
  );
  const dashboardIsLoading =
    portfolioInstancesQuery.isLoading ||
    positionsQuery.isLoading ||
    portfolioLivePositionsQuery.isLoading ||
    vaultsQuery.isLoading ||
    dashboardAssetSummariesQuery.isLoading ||
    performanceSeriesQuery.isLoading ||
    assetsQuery.isLoading ||
    assetPricesQuery.isLoading;
  const dashboardGraphQuery = useMemo(
    () => ({
      data: portfolios,
      error: combineCollectionQueryError(
        [
          {
            collection: collections?.portfolioInstances,
            enabled: input.enabled,
            error: getCollectionLastError(collections?.portfolioInstances),
            isLoading: portfolioInstancesQuery.isLoading,
            status: portfolioInstancesQuery.status,
          },
          {
            collection: collections?.positions,
            enabled: input.enabled && portfolioIds.length > 0,
            error: getCollectionLastError(collections?.positions),
            isLoading: positionsQuery.isLoading,
            status: positionsQuery.status,
          },
          {
            collection: collections?.portfolioLivePositions,
            enabled: input.enabled && portfolioIds.length > 0,
            error: getCollectionLastError(collections?.portfolioLivePositions),
            isLoading: portfolioLivePositionsQuery.isLoading,
            status: portfolioLivePositionsQuery.status,
          },
          {
            collection: collections?.dashboardAssetSummaries,
            enabled: input.enabled && portfolioIds.length > 0,
            error: getCollectionLastError(collections?.dashboardAssetSummaries),
            isLoading: dashboardAssetSummariesQuery.isLoading,
            status: dashboardAssetSummariesQuery.status,
          },
          {
            collection: collections?.vaults,
            enabled: input.enabled && portfolioIds.length > 0,
            error: getCollectionLastError(collections?.vaults),
            isLoading: vaultsQuery.isLoading,
            status: vaultsQuery.status,
          },
          {
            collection: collections?.performanceSeries,
            enabled: input.enabled && portfolioIds.length > 0,
            error: getCollectionLastError(collections?.performanceSeries),
            isLoading: performanceSeriesQuery.isLoading,
            status: performanceSeriesQuery.status,
          },
          {
            collection: collections?.assets,
            enabled: input.enabled && assetIds.length > 0,
            error: getCollectionLastError(collections?.assets),
            isLoading: assetsQuery.isLoading,
            status: assetsQuery.status,
          },
          {
            collection: collections?.assetPrices,
            enabled: input.enabled && assetIds.length > 0,
            error: getCollectionLastError(collections?.assetPrices),
            isLoading: assetPricesQuery.isLoading,
            status: assetPricesQuery.status,
          },
        ],
        "Failed to load portfolio dashboard data.",
      ),
      isLoading: dashboardIsLoading,
      status: combineCollectionQueryStatus([
        {
          collection: collections?.portfolioInstances,
          enabled: input.enabled,
          isLoading: portfolioInstancesQuery.isLoading,
          status: portfolioInstancesQuery.status,
        },
        {
          collection: collections?.positions,
          enabled: input.enabled && portfolioIds.length > 0,
          isLoading: positionsQuery.isLoading,
          status: positionsQuery.status,
        },
        {
          collection: collections?.portfolioLivePositions,
          enabled: input.enabled && portfolioIds.length > 0,
          isLoading: portfolioLivePositionsQuery.isLoading,
          status: portfolioLivePositionsQuery.status,
        },
        {
          collection: collections?.dashboardAssetSummaries,
          enabled: input.enabled && portfolioIds.length > 0,
          isLoading: dashboardAssetSummariesQuery.isLoading,
          status: dashboardAssetSummariesQuery.status,
        },
        {
          collection: collections?.vaults,
          enabled: input.enabled && portfolioIds.length > 0,
          isLoading: vaultsQuery.isLoading,
          status: vaultsQuery.status,
        },
        {
          collection: collections?.performanceSeries,
          enabled: input.enabled && portfolioIds.length > 0,
          isLoading: performanceSeriesQuery.isLoading,
          status: performanceSeriesQuery.status,
        },
        {
          collection: collections?.assets,
          enabled: input.enabled && assetIds.length > 0,
          isLoading: assetsQuery.isLoading,
          status: assetsQuery.status,
        },
        {
          collection: collections?.assetPrices,
          enabled: input.enabled && assetIds.length > 0,
          isLoading: assetPricesQuery.isLoading,
          status: assetPricesQuery.status,
        },
      ]),
    }),
    [
      assetIds.length,
      assetPricesQuery.isLoading,
      assetPricesQuery.status,
      assetsQuery.isLoading,
      assetsQuery.status,
      dashboardAssetSummariesQuery.isLoading,
      dashboardAssetSummariesQuery.status,
      dashboardIsLoading,
      input.enabled,
      portfolioIds.length,
      portfolioLivePositionsQuery.isLoading,
      portfolioLivePositionsQuery.status,
      portfolioInstancesQuery.isLoading,
      portfolioInstancesQuery.status,
      performanceSeriesQuery.isLoading,
      performanceSeriesQuery.status,
      portfolios,
      positionsQuery.isLoading,
      positionsQuery.status,
      vaultsQuery.isLoading,
      vaultsQuery.status,
    ],
  );
  const dashboardError = dashboardGraphQuery.error;

  return {
    assetPrices,
    assets,
    counts: {
      assetCount: assetIds.length,
      dashboardAssetSummaryCount: dashboardAssetSummaries.length,
      portfolioCount: portfolioInstances.length,
      positionCount: positions.length,
      vaultCount: vaults.length,
    },
    dashboardGraphQuery,
    dashboardAssetSummaries,
    error: dashboardError,
    isLoading: dashboardIsLoading,
    ownership,
    performanceSeries,
    portfolioInstances,
    portfolios,
    positions,
    vaults,
  };
}

export function usePortfolioDomainSummarySubscription(input: {
  enabled: boolean;
  includePerformanceSeries?: boolean;
  ownerAddresses?: string[];
  performanceMaxPointsPerPortfolio?: number;
  performanceResolution?: PerformanceChartResolution;
  strategyInstanceIds?: string[];
}) {
  const normalizedOwnerAddresses = useMemo(
    () => normalizeAddressList(input.ownerAddresses ?? []),
    [input.ownerAddresses],
  );
  const scopedStrategyInstanceIds = useMemo(
    () => normalizeNonEmptyIds(input.strategyInstanceIds),
    [input.strategyInstanceIds],
  );
  const sessionScope = useAppSessionScope();
  const ownerAddressesCacheKey = normalizedOwnerAddresses.join("|");
  const scopedStrategyIdsCacheKey = scopedStrategyInstanceIds.join("|");
  const [, rerenderAfterPerformanceHydration] = useReducer(
    (version: number) => version + 1,
    0,
  );
  const shouldSubscribeToSummary =
    input.enabled &&
    (normalizedOwnerAddresses.length > 0 ||
      scopedStrategyInstanceIds.length > 0);
  const collections = useMemo(
    () =>
      shouldSubscribeToSummary
        ? getPortfolioDomainCollections({
            ownerAddresses: normalizedOwnerAddresses,
            sessionScopeToken: sessionScope.scopeToken,
          })
        : null,
    [
      normalizedOwnerAddresses,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );
  usePortfolioGraphStoreSubscription({
    enabled: shouldSubscribeToSummary,
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });

  const portfolioInstancesQuery = useLiveQuery(
    (queryBuilder) => {
      if (!shouldSubscribeToSummary || !collections) {
        return undefined;
      }

      const baseQuery = queryBuilder.from({
        portfolioInstance: collections.portfolioInstances,
      });

      if (scopedStrategyInstanceIds.length > 0) {
        return baseQuery.where(({ portfolioInstance }) =>
          inArray(portfolioInstance.id, scopedStrategyInstanceIds),
        );
      }

      if (normalizedOwnerAddresses.length === 0) {
        return baseQuery;
      }

      return normalizedOwnerAddresses.length === 1
        ? baseQuery.where(({ portfolioInstance }) =>
            eq(portfolioInstance.ownerAddress, normalizedOwnerAddresses[0]!),
          )
        : baseQuery.where(({ portfolioInstance }) =>
            inArray(portfolioInstance.ownerAddress, normalizedOwnerAddresses),
          );
    },
    [
      collections,
      ownerAddressesCacheKey,
      scopedStrategyIdsCacheKey,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );
  const portfolioInstances = useMemo(
    () => dedupeById(portfolioInstancesQuery.data ?? []),
    [portfolioInstancesQuery.data],
  );
  const portfolioIds = useMemo(
    () =>
      normalizeNonEmptyIds(portfolioInstances.map((portfolio) => portfolio.id)),
    [portfolioInstances],
  );
  const portfolioIdsCacheKey = portfolioIds.join("|");
  useEffect(() => {
    if (
      !shouldSubscribeToSummary ||
      input.includePerformanceSeries !== true ||
      portfolioIdsCacheKey.length === 0
    ) {
      return;
    }

    // Key this sweep on the CONTENT of the id/owner sets, not their array
    // identities: the ids derive from a live query that emits a fresh array
    // whenever any writer touches portfolioInstances — the 30s focused-detail
    // seed included — and an identity dep re-fired this full all-portfolios
    // sparkline sweep (network + a ~1s main-thread apply) on every tick.
    const hydrationPortfolioIds = portfolioIdsCacheKey.split("|");
    const hydrationOwnerAddresses =
      ownerAddressesCacheKey.length === 0
        ? []
        : ownerAddressesCacheKey.split("|");

    let cancelled = false;
    void hydratePortfolioSummaries(hydrationPortfolioIds, {
      includeAssetPrices: false,
      includeAssets: false,
      includeDashboardAssetSummaries: false,
      includePerformance: true,
      includePortfolioInstances: false,
      includePositions: false,
      includeVaults: false,
      ownerAddresses: hydrationOwnerAddresses,
      performanceMaxPointsPerPortfolio: input.performanceMaxPointsPerPortfolio,
      performanceResolution: input.performanceResolution,
      sessionScopeToken: sessionScope.scopeToken,
    })
      .then(() => {
        if (!cancelled) {
          rerenderAfterPerformanceHydration();
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [
    input.includePerformanceSeries,
    input.performanceMaxPointsPerPortfolio,
    input.performanceResolution,
    ownerAddressesCacheKey,
    portfolioIdsCacheKey,
    sessionScope.scopeToken,
    shouldSubscribeToSummary,
  ]);

  const positionsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldSubscribeToSummary ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ position: collections.positions })
            .where(({ position }) => eq(position.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ position: collections.positions })
            .where(({ position }) =>
              inArray(position.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );
  const positions = useMemo(
    () => dedupeById(positionsQuery.data ?? []),
    [positionsQuery.data],
  );

  const portfolioLivePositionsQuery = useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldSubscribeToSummary ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ position: collections.portfolioLivePositions })
            .where(({ position }) => eq(position.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ position: collections.portfolioLivePositions })
            .where(({ position }) =>
              inArray(position.portfolioId, portfolioIds),
            );
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );
  const portfolioLivePositions = useMemo(
    () => dedupeById(portfolioLivePositionsQuery.data ?? []),
    [portfolioLivePositionsQuery.data],
  );

  useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldSubscribeToSummary ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ summary: collections.dashboardAssetSummaries })
            .where(({ summary }) => eq(summary.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ summary: collections.dashboardAssetSummaries })
            .where(({ summary }) => inArray(summary.portfolioId, portfolioIds));
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );

  useLiveQuery(
    (queryBuilder) => {
      if (
        !shouldSubscribeToSummary ||
        !collections ||
        portfolioIds.length === 0
      ) {
        return undefined;
      }

      return portfolioIds.length === 1
        ? queryBuilder
            .from({ vault: collections.vaults })
            .where(({ vault }) => eq(vault.portfolioId, portfolioIds[0]!))
        : queryBuilder
            .from({ vault: collections.vaults })
            .where(({ vault }) => inArray(vault.portfolioId, portfolioIds));
    },
    [
      collections,
      portfolioIdsCacheKey,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );

  const assetIds = useMemo(
    () =>
      normalizeNonEmptyIds(
        [...positions, ...portfolioLivePositions].map(
          (position) => position.assetId,
        ),
      ),
    [portfolioLivePositions, positions],
  );
  const assetIdsCacheKey = assetIds.join("|");

  useLiveQuery(
    (queryBuilder) => {
      if (!shouldSubscribeToSummary || !collections || assetIds.length === 0) {
        return undefined;
      }

      return assetIds.length === 1
        ? queryBuilder
            .from({ asset: collections.assets })
            .where(({ asset }) => eq(asset.id, assetIds[0]!))
        : queryBuilder
            .from({ asset: collections.assets })
            .where(({ asset }) => inArray(asset.id, assetIds));
    },
    [
      assetIdsCacheKey,
      collections,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );

  useLiveQuery(
    (queryBuilder) => {
      if (!shouldSubscribeToSummary || !collections || assetIds.length === 0) {
        return undefined;
      }

      return assetIds.length === 1
        ? queryBuilder
            .from({ assetPrice: collections.assetPrices })
            .where(({ assetPrice }) => eq(assetPrice.assetId, assetIds[0]!))
        : queryBuilder
            .from({ assetPrice: collections.assetPrices })
            .where(({ assetPrice }) => inArray(assetPrice.assetId, assetIds));
    },
    [
      assetIdsCacheKey,
      collections,
      sessionScope.scopeToken,
      shouldSubscribeToSummary,
    ],
  );
}

function subscribeToPortfolioCollections(input: {
  collections: PortfolioDomainCollections | null;
  enabled: boolean;
  onStoreChange: () => void;
}) {
  if (!input.enabled || !input.collections) {
    return () => {};
  }

  const subscriptions = [
    input.collections.portfolioInstances,
    input.collections.positions,
    input.collections.portfolioLivePositions,
    input.collections.dashboardAssetSummaries,
    input.collections.vaults,
    input.collections.performanceSeries,
    input.collections.assets,
    input.collections.assetPrices,
    input.collections.assetPnl,
  ].map((collection) =>
    collection.subscribeChanges(() => {
      input.onStoreChange();
    }),
  );

  return () => {
    subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  };
}

function readScopedRows<T extends { id: string }>(input: {
  collection: { values: () => IterableIterator<T> } | undefined;
  enabled: boolean;
  ids?: ReadonlySet<string>;
}): T[] {
  if (!input.enabled || !input.collection) {
    return [];
  }

  const rows = Array.from(input.collection.values());
  if (!input.ids || input.ids.size === 0) {
    return dedupeById(rows);
  }

  return dedupeById(rows.filter((row) => input.ids!.has(row.id)));
}

function usePortfolioCollectionVersion(input: {
  collections: PortfolioDomainCollections | null;
  enabled: boolean;
}) {
  const versionRef = useRef(0);

  return useSyncExternalStore(
    (onStoreChange) =>
      subscribeToPortfolioCollections({
        collections: input.collections,
        enabled: input.enabled,
        onStoreChange: () => {
          versionRef.current += 1;
          onStoreChange();
        },
      }),
    () => versionRef.current,
    () => 0,
  );
}

export function useSeededPortfolioDomainSlice(input: {
  enabled: boolean;
  ownerAddresses?: string[];
  strategyInstanceIds?: string[];
}) {
  const normalizedOwnerAddresses = useMemo(
    () => normalizeAddressList(input.ownerAddresses ?? []),
    [input.ownerAddresses],
  );
  const ownerAddressSet = useMemo(
    () => new Set(normalizedOwnerAddresses),
    [normalizedOwnerAddresses],
  );
  const scopedStrategyInstanceIds = useMemo(
    () => normalizeNonEmptyIds(input.strategyInstanceIds),
    [input.strategyInstanceIds],
  );
  const scopedStrategyInstanceIdSet = useMemo(
    () =>
      scopedStrategyInstanceIds.length > 0
        ? new Set(scopedStrategyInstanceIds)
        : null,
    [scopedStrategyInstanceIds],
  );
  const sessionScope = useAppSessionScope();
  const shouldReadSeededCollections = input.enabled;
  const collections = useMemo(
    () =>
      shouldReadSeededCollections
        ? getPortfolioDomainCollections({
            ownerAddresses: normalizedOwnerAddresses,
            sessionScopeToken: sessionScope.scopeToken,
          })
        : null,
    [
      normalizedOwnerAddresses,
      sessionScope.scopeToken,
      shouldReadSeededCollections,
    ],
  );
  usePortfolioGraphStoreSubscription({
    enabled: shouldReadSeededCollections,
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });
  const version = usePortfolioCollectionVersion({
    collections,
    enabled: shouldReadSeededCollections,
  });

  const portfolioInstances = useMemo(
    () =>
      readScopedRows({
        collection: collections?.portfolioInstances,
        enabled: shouldReadSeededCollections,
        ids: scopedStrategyInstanceIdSet ?? undefined,
      }),
    [
      collections?.portfolioInstances,
      scopedStrategyInstanceIdSet,
      shouldReadSeededCollections,
      version,
    ],
  );
  const portfolioIds = useMemo(
    () =>
      normalizeNonEmptyIds(portfolioInstances.map((portfolio) => portfolio.id)),
    [portfolioInstances],
  );
  const portfolioIdSet = useMemo(() => new Set(portfolioIds), [portfolioIds]);
  const positions = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.positions.values()).filter((position) =>
              portfolioIdSet.has(position.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const portfolioLivePositions = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(
              collections.portfolioLivePositions?.values() ?? [],
            ).filter((position) => portfolioIdSet.has(position.portfolioId)),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const vaults = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.vaults.values()).filter((vault) =>
              portfolioIdSet.has(vault.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const dashboardAssetSummaries = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.dashboardAssetSummaries.values()).filter(
              (summary) => portfolioIdSet.has(summary.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const performanceSeries = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.performanceSeries.values()).filter((point) =>
              portfolioIdSet.has(point.portfolioId),
            ),
          ).sort((left, right) => left.timestamp - right.timestamp)
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const performanceWindowStats = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(
              collections.performanceWindowStats?.values() ?? [],
            ).filter((windowStats) =>
              portfolioIdSet.has(windowStats.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const portfolioSchedules = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.portfolioSchedules?.values() ?? []).filter(
              (schedule) => portfolioIdSet.has(schedule.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const assetIds = useMemo(
    () =>
      normalizeNonEmptyIds(
        [...positions, ...portfolioLivePositions].map(
          (position) => position.assetId,
        ),
      ),
    [portfolioLivePositions, positions],
  );
  const assetIdSet = useMemo(() => new Set(assetIds), [assetIds]);
  const assets = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.assets.values()).filter((asset) =>
              assetIdSet.has(asset.id),
            ),
          )
        : [],
    [assetIdSet, collections, shouldReadSeededCollections, version],
  );
  const assetPnl = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.assetPnl.values()).filter((row) =>
              portfolioIdSet.has(row.portfolioId),
            ),
          )
        : [],
    [collections, portfolioIdSet, shouldReadSeededCollections, version],
  );
  const assetPrices = useMemo(
    () =>
      shouldReadSeededCollections && collections
        ? dedupeById(
            Array.from(collections.assetPrices.values()).filter((assetPrice) =>
              assetIdSet.has(assetPrice.assetId),
            ),
          )
        : [],
    [assetIdSet, collections, shouldReadSeededCollections, version],
  );

  const assetById = useMemo(() => mapRowsById(assets), [assets]);
  const assetPriceByAssetId = useMemo(
    () => mapAssetPricesByAssetId(assetPrices),
    [assetPrices],
  );
  const positionsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(positions),
    [positions],
  );
  const portfolioLivePositionsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(portfolioLivePositions),
    [portfolioLivePositions],
  );
  const vaultsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(vaults),
    [vaults],
  );
  const dashboardAssetSummariesByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(dashboardAssetSummaries),
    [dashboardAssetSummaries],
  );
  const performanceSeriesByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(performanceSeries),
    [performanceSeries],
  );
  const performanceWindowStatsByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(performanceWindowStats),
    [performanceWindowStats],
  );
  const portfolioSchedulesByPortfolioId = useMemo(
    () => groupRowsByPortfolioId(portfolioSchedules),
    [portfolioSchedules],
  );
  const portfolios = useMemo<PortfolioDashboardGraphRow[]>(
    () =>
      portfolioInstances.map((portfolio) => ({
        ...portfolio,
        dashboardAssetSummaries:
          dashboardAssetSummariesByPortfolioId.get(portfolio.id) ?? [],
        liveTokenRows: buildTokenRows({
          assetById,
          assetPriceByAssetId,
          positions:
            portfolioLivePositionsByPortfolioId.get(portfolio.id) ?? [],
        }),
        performanceSeries:
          performanceSeriesByPortfolioId.get(portfolio.id) ?? [],
        performanceWindowStats: performanceWindowStatsByPortfolioId.get(
          portfolio.id,
        )?.[0],
        portfolioSchedule: portfolioSchedulesByPortfolioId.get(
          portfolio.id,
        )?.[0],
        tokenRows: buildTokenRows({
          assetById,
          assetPriceByAssetId,
          positions: positionsByPortfolioId.get(portfolio.id) ?? [],
        }),
        vaults: vaultsByPortfolioId.get(portfolio.id) ?? [],
      })),
    [
      assetById,
      assetPriceByAssetId,
      dashboardAssetSummariesByPortfolioId,
      performanceSeriesByPortfolioId,
      performanceWindowStatsByPortfolioId,
      portfolioLivePositionsByPortfolioId,
      portfolioInstances,
      portfolioSchedulesByPortfolioId,
      positionsByPortfolioId,
      vaultsByPortfolioId,
    ],
  );
  const ownership = useMemo(
    () => createOwnershipSummary(portfolioInstances, ownerAddressSet),
    [ownerAddressSet, portfolioInstances],
  );
  const rawDashboardError =
    getCollectionLastError(collections?.portfolioInstances) ??
    getCollectionLastError(collections?.dashboardAssetSummaries) ??
    getCollectionLastError(collections?.positions) ??
    getCollectionLastError(collections?.portfolioLivePositions) ??
    getCollectionLastError(collections?.vaults) ??
    getCollectionLastError(collections?.performanceSeries) ??
    getCollectionLastError(collections?.assets) ??
    getCollectionLastError(collections?.assetPrices);
  const dashboardError = useMemo(
    () =>
      rawDashboardError
        ? normalizeCollectionError(
            rawDashboardError,
            "Failed to load portfolio dashboard data.",
          )
        : null,
    [rawDashboardError, version],
  );

  return {
    assetPnl,
    assetPrices,
    assets,
    counts: {
      assetCount: assetIds.length,
      dashboardAssetSummaryCount: dashboardAssetSummaries.length,
      portfolioCount: portfolioInstances.length,
      positionCount: positions.length,
      vaultCount: vaults.length,
    },
    dashboardGraphQuery: {
      data: portfolios,
      error: dashboardError,
      isLoading: false,
      status: dashboardError ? ("error" as const) : ("success" as const),
    },
    dashboardAssetSummaries,
    error: dashboardError,
    isLoading: false,
    ownership,
    performanceSeries,
    portfolioInstances,
    portfolioSchedules,
    portfolios,
    positions,
    vaults,
  };
}

export function usePortfolioDomainDetailSlice(input: {
  enabled: boolean;
  historyLimit?: number;
  ownerAddresses?: string[];
  portfolioId: string | null;
}) {
  const normalizedOwnerAddresses = useMemo(
    () => normalizeAddressList(input.ownerAddresses ?? []),
    [input.ownerAddresses],
  );
  const ownerAddressScopeKey = normalizedOwnerAddresses.join("|");
  const normalizedPortfolioId = input.portfolioId?.trim() ?? "";
  const portfolioId =
    normalizedPortfolioId.length > 0 ? normalizedPortfolioId : null;
  const historyLimit = normalizePositiveInteger(input.historyLimit, 30);
  const sessionScope = useAppSessionScope();
  const collections = useMemo(
    () =>
      getPortfolioDomainCollections({
        ownerAddresses: normalizedOwnerAddresses,
        sessionScopeToken: sessionScope.scopeToken,
      }),
    [ownerAddressScopeKey, sessionScope.scopeToken],
  );

  const detailRowsEnabled = input.enabled && portfolioId !== null;
  usePortfolioGraphStoreSubscription({
    enabled: detailRowsEnabled,
    ownerAddresses: normalizedOwnerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });
  const detailViewQuery = useLiveQuery(
    (queryBuilder) => {
      if (!detailRowsEnabled || !portfolioId) {
        return undefined;
      }

      return buildPortfolioDetailViewQuery({
        collections,
        historyLimit,
        portfolioId,
      })(queryBuilder);
    },
    [
      detailRowsEnabled,
      historyLimit,
      ownerAddressScopeKey,
      portfolioId,
      sessionScope.scopeToken,
    ],
  );
  const detailView = detailViewQuery.data;
  const tokenRows = useMemo<PortfolioTokenGraphRow[]>(
    () =>
      (detailView?.tokenRows ?? []).map((tokenRow) => ({
        asset: isPortfolioAssetModel(tokenRow.asset)
          ? tokenRow.asset
          : undefined,
        assetPrice: isPortfolioAssetPriceModel(tokenRow.assetPrice)
          ? tokenRow.assetPrice
          : undefined,
        position: tokenRow.position,
      })),
    [detailView?.tokenRows],
  );
  const liveTokenRows = useMemo<PortfolioTokenGraphRow[]>(
    () =>
      (detailView?.liveTokenRows ?? []).map((tokenRow) => ({
        asset: isPortfolioAssetModel(tokenRow.asset)
          ? tokenRow.asset
          : undefined,
        assetPrice: isPortfolioAssetPriceModel(tokenRow.assetPrice)
          ? tokenRow.assetPrice
          : undefined,
        position: tokenRow.position,
      })),
    [detailView?.liveTokenRows],
  );
  const portfolioAssetRows = useMemo<PortfolioTokenGraphRow[]>(
    () =>
      (detailView?.portfolioAssetRows ?? []).map((assetRow) => ({
        asset: isPortfolioAssetModel(assetRow.asset)
          ? assetRow.asset
          : undefined,
        assetPrice: isPortfolioAssetPriceModel(assetRow.assetPrice)
          ? assetRow.assetPrice
          : undefined,
        position: {
          assetId: assetRow.link.assetId,
          id: assetRow.link.id,
          portfolioId: assetRow.link.portfolioId,
          quantity: "0",
          updatedAt: assetRow.link.updatedAt,
        },
      })),
    [detailView?.portfolioAssetRows],
  );
  const positions = useMemo(
    () => dedupeById(tokenRows.map((tokenRow) => tokenRow.position)),
    [tokenRows],
  );
  const assets = useMemo(
    () =>
      dedupeById(
        tokenRows.flatMap((tokenRow) =>
          tokenRow.asset ? [tokenRow.asset] : [],
        ),
      ),
    [tokenRows],
  );
  const assetPrices = useMemo(
    () =>
      dedupeById(
        tokenRows.flatMap((tokenRow) =>
          tokenRow.assetPrice ? [tokenRow.assetPrice] : [],
        ),
      ),
    [tokenRows],
  );
  const portfolioAssets = useMemo(
    () =>
      dedupeById(
        portfolioAssetRows.flatMap((assetRow) =>
          assetRow.asset ? [assetRow.asset] : [],
        ),
      ),
    [portfolioAssetRows],
  );
  const portfolioAssetPrices = useMemo(
    () =>
      dedupeById(
        portfolioAssetRows.flatMap((assetRow) =>
          assetRow.assetPrice ? [assetRow.assetPrice] : [],
        ),
      ),
    [portfolioAssetRows],
  );
  const vaults = useMemo(
    () => dedupeById(detailView?.vaults ?? []),
    [detailView?.vaults],
  );
  const assetPnl = useMemo(
    () => dedupeById(detailView?.assetPnl ?? []),
    [detailView?.assetPnl],
  );
  const historyEvents = useMemo(
    () => dedupeById(detailView?.historyEvents ?? []),
    [detailView?.historyEvents],
  );
  const performancePoints = useMemo(
    () => dedupeById(detailView?.performancePoints ?? []),
    [detailView?.performancePoints],
  );
  const performanceSeries = useMemo(
    () => dedupeById(detailView?.performanceSeries ?? []),
    [detailView?.performanceSeries],
  );
  const portfolio = useMemo<PortfolioDetailGraphRow | undefined>(() => {
    const data = detailView?.portfolioInstance;
    if (!data) {
      return undefined;
    }

    return {
      ...data,
      assetPnl,
      historyEvents: [...historyEvents].sort(
        (left, right) => right.timestamp - left.timestamp,
      ),
      liveTokenRows,
      performancePoints: [...performancePoints].sort(
        (left, right) => left.timestamp - right.timestamp,
      ),
      performanceSeries: [...performanceSeries].sort(
        (left, right) => left.timestamp - right.timestamp,
      ),
      performanceSummary: detailView?.performanceSummary,
      performanceWindowStats: detailView?.performanceWindowStats,
      portfolioAssetRows,
      portfolioSchedule: detailView?.portfolioSchedule,
      tokenRows,
      vaults,
    };
  }, [
    assetPnl,
    detailView?.portfolioInstance,
    historyEvents,
    liveTokenRows,
    performancePoints,
    performanceSeries,
    detailView?.performanceSummary,
    detailView?.performanceWindowStats,
    portfolioAssetRows,
    detailView?.portfolioSchedule,
    tokenRows,
    vaults,
  ]);
  const detailGraphStatus = combineCollectionQueryStatus([
    {
      collection: collections.portfolioInstances,
      enabled: detailRowsEnabled,
      isLoading: detailViewQuery.isLoading,
      status: detailViewQuery.status,
    },
  ]);
  const detailIsLoading = detailGraphStatus === "pending";
  const detailGraphQuery = useMemo(
    () => ({
      data: portfolio,
      error: combineCollectionQueryError(
        [
          {
            collection: collections.portfolioInstances,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.portfolioInstances),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.positions,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.positions),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.portfolioAssetUniverse,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.portfolioAssetUniverse),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.portfolioLivePositions,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.portfolioLivePositions),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.assets,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.assets),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.assetPrices,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.assetPrices),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.vaults,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.vaults),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.assetPnl,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.assetPnl),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.historyEvents,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.historyEvents),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.performancePoints,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.performancePoints),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.performanceSeries,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.performanceSeries),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.performanceSummaries,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.performanceSummaries),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.performanceWindowStats,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.performanceWindowStats),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
          {
            collection: collections.portfolioSchedules,
            enabled: detailRowsEnabled,
            error: getCollectionLastError(collections.portfolioSchedules),
            isLoading: detailViewQuery.isLoading,
            status: detailViewQuery.status,
          },
        ],
        "Failed to load portfolio detail data.",
      ),
      isLoading: detailIsLoading,
      status: detailGraphStatus,
    }),
    [
      detailGraphStatus,
      detailIsLoading,
      detailRowsEnabled,
      portfolio,
      detailViewQuery.status,
    ],
  );
  const detailError = detailGraphQuery.error;

  return {
    assetPnl,
    assetPrices,
    assets,
    detailGraphQuery,
    error: detailError,
    historyEvents,
    isLoading: detailIsLoading,
    liveTokenRows,
    performancePoints,
    performanceSeries,
    performanceSummary: detailView?.performanceSummary,
    performanceWindowStats: detailView?.performanceWindowStats,
    portfolio,
    portfolioAssetPrices,
    portfolioAssetRows,
    portfolioAssets,
    portfolioId,
    portfolioInstance: portfolio
      ? {
          archived: portfolio.archived,
          blueprintEditId: portfolio.blueprintEditId,
          blueprintId: portfolio.blueprintId,
          canonicalStrategyBlueprintId: portfolio.canonicalStrategyBlueprintId,
          controlPlane: portfolio.controlPlane,
          createdAt: portfolio.createdAt,
          description: portfolio.description,
          forkedFromBlueprintId: portfolio.forkedFromBlueprintId,
          id: portfolio.id,
          isMirrored: portfolio.isMirrored,
          isPublic: portfolio.isPublic,
          name: portfolio.name,
          ownerAddress: portfolio.ownerAddress,
          ownerVm: portfolio.ownerVm,
          strategyData: portfolio.strategyData,
          updatedAt: portfolio.updatedAt,
          viewerCanManage: portfolio.viewerCanManage,
          viewerIsOwner: portfolio.viewerIsOwner,
        }
      : undefined,
    portfolioSchedule: detailView?.portfolioSchedule,
    positions,
    vaults,
  };
}

export function usePortfolioWorkflowsSlice(input: {
  enabled: boolean;
  kind?: string;
  ownerAddresses?: string[];
  portfolioId: string | null;
  status?: string;
}) {
  const normalizedPortfolioId = input.portfolioId?.trim() ?? "";
  const portfolioId =
    normalizedPortfolioId.length > 0 ? normalizedPortfolioId : null;
  const sessionScope = useAppSessionScope();
  const ownerAddressScopeKey = (input.ownerAddresses ?? []).join("|");
  const collections = useMemo(
    () =>
      getPortfolioDomainCollections({
        ownerAddresses: input.ownerAddresses,
        sessionScopeToken: sessionScope.scopeToken,
      }),
    [ownerAddressScopeKey, sessionScope.scopeToken],
  );

  const rowsEnabled = input.enabled && portfolioId !== null;
  usePortfolioGraphStoreSubscription({
    enabled: rowsEnabled,
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });
  const workflowsQuery = useLiveQuery(
    (queryBuilder) => {
      if (!rowsEnabled || !portfolioId) {
        return undefined;
      }

      const kind = input.kind;
      const statusFilter = input.status;

      if (kind && statusFilter) {
        return queryBuilder
          .from({ workflow: collections.portfolioWorkflows })
          .where(({ workflow }) => eq(workflow.portfolioId, portfolioId))
          .where(({ workflow }) => eq(workflow.kind, kind))
          .where(({ workflow }) => eq(workflow.status, statusFilter))
          .orderBy(({ workflow }) => workflow.updatedAt, "desc")
          .select(({ workflow }) => workflow);
      }

      if (kind) {
        return queryBuilder
          .from({ workflow: collections.portfolioWorkflows })
          .where(({ workflow }) => eq(workflow.portfolioId, portfolioId))
          .where(({ workflow }) => eq(workflow.kind, kind))
          .orderBy(({ workflow }) => workflow.updatedAt, "desc")
          .select(({ workflow }) => workflow);
      }

      if (statusFilter) {
        return queryBuilder
          .from({ workflow: collections.portfolioWorkflows })
          .where(({ workflow }) => eq(workflow.portfolioId, portfolioId))
          .where(({ workflow }) => eq(workflow.status, statusFilter))
          .orderBy(({ workflow }) => workflow.updatedAt, "desc")
          .select(({ workflow }) => workflow);
      }

      return queryBuilder
        .from({ workflow: collections.portfolioWorkflows })
        .where(({ workflow }) => eq(workflow.portfolioId, portfolioId))
        .orderBy(({ workflow }) => workflow.updatedAt, "desc")
        .select(({ workflow }) => workflow);
    },
    [
      input.kind,
      input.status,
      rowsEnabled,
      portfolioId,
      ownerAddressScopeKey,
      sessionScope.scopeToken,
    ],
  );

  const workflows = useMemo<PortfolioWorkflowModel[]>(
    () =>
      dedupeById(workflowsQuery.data ?? []).filter(
        (workflow) =>
          (input.kind === undefined || workflow.kind === input.kind) &&
          (input.status === undefined || workflow.status === input.status),
      ),
    [input.kind, input.status, workflowsQuery.data],
  );
  const status = combineCollectionQueryStatus([
    {
      collection: collections.portfolioWorkflows,
      enabled: rowsEnabled,
      isLoading: workflowsQuery.isLoading,
      status: workflowsQuery.status,
    },
  ]);
  const error = combineCollectionQueryError(
    [
      {
        collection: collections.portfolioWorkflows,
        enabled: rowsEnabled,
        error: getCollectionLastError(collections.portfolioWorkflows),
        isLoading: workflowsQuery.isLoading,
        status: workflowsQuery.status,
      },
    ],
    "Failed to load portfolio workflows.",
  );

  return {
    error,
    isLoading: status === "pending",
    portfolioId,
    status,
    workflows,
  };
}

export function usePortfolioScheduledFunctionsSlice(input: {
  enabled: boolean;
  functionKey?: string;
  ownerAddresses?: string[];
  portfolioId: string | null;
}) {
  const normalizedPortfolioId = input.portfolioId?.trim() ?? "";
  const portfolioId =
    normalizedPortfolioId.length > 0 ? normalizedPortfolioId : null;
  const sessionScope = useAppSessionScope();
  const ownerAddressScopeKey = (input.ownerAddresses ?? []).join("|");
  const collections = useMemo(
    () =>
      getPortfolioDomainCollections({
        ownerAddresses: input.ownerAddresses,
        sessionScopeToken: sessionScope.scopeToken,
      }),
    [ownerAddressScopeKey, sessionScope.scopeToken],
  );

  const rowsEnabled = input.enabled && portfolioId !== null;
  usePortfolioGraphStoreSubscription({
    enabled: rowsEnabled,
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });
  const scheduledFunctionsQuery = useLiveQuery(
    (queryBuilder) => {
      if (!rowsEnabled || !portfolioId) {
        return undefined;
      }

      const functionKey = input.functionKey;
      if (functionKey) {
        return queryBuilder
          .from({
            scheduledFunction: collections.portfolioScheduledFunctions,
          })
          .where(({ scheduledFunction }) =>
            eq(scheduledFunction.portfolioId, portfolioId),
          )
          .where(({ scheduledFunction }) =>
            eq(scheduledFunction.functionKey, functionKey),
          )
          .orderBy(
            ({ scheduledFunction }) => scheduledFunction.updatedAt,
            "desc",
          )
          .select(({ scheduledFunction }) => scheduledFunction);
      }

      return queryBuilder
        .from({
          scheduledFunction: collections.portfolioScheduledFunctions,
        })
        .where(({ scheduledFunction }) =>
          eq(scheduledFunction.portfolioId, portfolioId),
        )
        .orderBy(({ scheduledFunction }) => scheduledFunction.updatedAt, "desc")
        .select(({ scheduledFunction }) => scheduledFunction);
    },
    [
      input.functionKey,
      rowsEnabled,
      portfolioId,
      ownerAddressScopeKey,
      sessionScope.scopeToken,
    ],
  );

  const scheduledFunctions = useMemo<PortfolioScheduledFunctionModel[]>(
    () =>
      dedupeById(scheduledFunctionsQuery.data ?? []).filter(
        (scheduledFunction) =>
          input.functionKey === undefined ||
          scheduledFunction.functionKey === input.functionKey,
      ),
    [input.functionKey, scheduledFunctionsQuery.data],
  );
  const status = combineCollectionQueryStatus([
    {
      collection: collections.portfolioScheduledFunctions,
      enabled: rowsEnabled,
      isLoading: scheduledFunctionsQuery.isLoading,
      status: scheduledFunctionsQuery.status,
    },
  ]);
  const error = combineCollectionQueryError(
    [
      {
        collection: collections.portfolioScheduledFunctions,
        enabled: rowsEnabled,
        error: getCollectionLastError(collections.portfolioScheduledFunctions),
        isLoading: scheduledFunctionsQuery.isLoading,
        status: scheduledFunctionsQuery.status,
      },
    ],
    "Failed to load portfolio scheduled functions.",
  );

  return {
    error,
    isLoading: status === "pending",
    portfolioId,
    scheduledFunctions,
    status,
  };
}

export function usePortfolioAllocationHistoryChartSlice(input: {
  enabled: boolean;
  ownerAddresses?: string[];
  portfolioId: string | null;
}) {
  const normalizedPortfolioId = input.portfolioId?.trim() ?? "";
  const portfolioId =
    normalizedPortfolioId.length > 0 ? normalizedPortfolioId : null;
  const sessionScope = useAppSessionScope();
  const collections = useMemo(
    () =>
      getPortfolioDomainCollections({
        ownerAddresses: input.ownerAddresses,
        sessionScopeToken: sessionScope.scopeToken,
      }),
    [input.ownerAddresses, sessionScope.scopeToken],
  );
  const rowsEnabled = input.enabled && portfolioId !== null;
  usePortfolioGraphStoreSubscription({
    enabled: rowsEnabled,
    ownerAddresses: input.ownerAddresses,
    sessionScopeToken: sessionScope.scopeToken,
  });
  const checkpointsQuery = useLiveQuery(
    (queryBuilder) => {
      if (!rowsEnabled || !portfolioId) {
        return undefined;
      }

      return queryBuilder
        .from({ checkpoint: collections.allocationHistoryCheckpoints })
        .where(({ checkpoint }) => eq(checkpoint.portfolioId, portfolioId))
        .orderBy(({ checkpoint }) => checkpoint.timestampMs, "asc")
        .select(({ checkpoint }) => checkpoint);
    },
    [rowsEnabled, portfolioId, sessionScope.scopeToken],
  );

  const checkpoints = useMemo<PortfolioAllocationHistoryCheckpointModel[]>(
    () =>
      dedupeById(checkpointsQuery.data ?? []).sort(
        (left, right) => left.timestampMs - right.timestampMs,
      ),
    [checkpointsQuery.data],
  );
  const status = combineCollectionQueryStatus([
    {
      collection: collections.allocationHistoryCheckpoints,
      enabled: rowsEnabled,
      isLoading: checkpointsQuery.isLoading,
      status: checkpointsQuery.status,
    },
  ]);
  const isLoading = status === "pending";
  const error = combineCollectionQueryError(
    [
      {
        collection: collections.allocationHistoryCheckpoints,
        enabled: rowsEnabled,
        error: getCollectionLastError(collections.allocationHistoryCheckpoints),
        isLoading: checkpointsQuery.isLoading,
        status: checkpointsQuery.status,
      },
    ],
    "Failed to load allocation history chart.",
  );

  return {
    checkpoints,
    error,
    isLoading,
    portfolioId,
    status,
  };
}
